import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { ApiError } from '../errors.js';
import { loadConfig } from '../config.js';
import { getFormat } from '../helpers.js';

interface ReportResponse {
  report_id: string;
  created: boolean;
  fingerprint: string;
}

// Reporter-facing projection (mirrors the backend's toReporterSummary — withholds gh_issue_url /
// ai_verdict). The CLI is a separate package and can't import backend internals.
interface ReporterSummary {
  report_id: string;
  classification: string | null;
  status: string;
  created_at: string;
  triaged_at: string | null;
  resolved_at: string | null;
  contest_count: number;
  dismissal_final: boolean;
}

interface ReportMessage {
  message_id: string;
  author_role: string;
  transition: string | null;
  body: string;
  created_at: string;
}

// Map the reporter verbs' ApiErrors to friendly one-liners + exit; re-throw anything else.
function reportActionError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.status === 404) {
      console.error('Report not found, or it was not filed by you.');
    } else if (err.status === 403) {
      console.error('This report is agent-authored and can only be handled by a platform admin.');
    } else if (err.status === 409) {
      // Conflict carries the actionable reason (wrong status, final dismissal, cap reached).
      console.error(err.message);
    } else if (err.status === 429) {
      console.error('Rate limit exceeded. Please try again later.');
    } else {
      console.error(`Request failed: ${err.message}`);
    }
    return process.exit(1);
  }
  throw err;
}

export const reportCommand = new Command('report')
  .description('File a report to the Rekor triage queue and verify the outcome');

reportCommand.command('create')
  .description('Submit a report to the Rekor triage queue (deduplicated)')
  .requiredOption('--title <title>', 'Short summary')
  .requiredOption('--description <text>', 'What happened')
  .option('--source <source>', 'Origin: cli_report (default) | review | security_audit')
  .option('--classification <c>', 'Nature: bug (default) | flaky_test | security')
  .option('--dedup-key <key>', 'Stable dedup key (e.g. "<file>::<test>"); collapses repeat occurrences')
  .option('--severity <level>', 'low | medium | high | critical')
  .option('--steps <text>', 'Steps to reproduce')
  .option('--error-message <text>', 'Exact error text if any')
  .option('--context <text>', 'Additional context (logs, request IDs)')
  .option('--locale <code>', 'Notification locale (en | pt | es)', 'en')
  .option('--reporter-email <addr>', 'Override reporter email (defaults to session email)')
  // Entity pointers: which database/record_type/document the error relates to.
  // Carried so triage can investigate the live data with `rekor admin`/`rekor sql`.
  .option('--base-id <id>', 'Base the error relates to (defaults to configured default_database)')
  .option('--record_type <name>', 'RecordType the error relates to')
  .option('--document-id <id>', 'Document id (UUID) the error relates to')
  .option('--external-id <id>', 'External id of the related document')
  .option('--external-source <src>', 'External source of the related document')
  .option('--relationship-id <id>', 'Relationship id the error relates to')
  .action(async function (
    this: Command,
    opts: {
      title: string;
      description: string;
      source?: string;
      classification?: string;
      dedupKey?: string;
      severity?: string;
      steps?: string;
      errorMessage?: string;
      context?: string;
      locale?: string;
      reporterEmail?: string;
      baseId?: string;
      record_type?: string;
      documentId?: string;
      externalId?: string;
      externalSource?: string;
      relationshipId?: string;
    },
  ) {
    const client = new ApiClient();
    const body: Record<string, unknown> = {
      title: opts.title,
      description: opts.description,
      locale: opts.locale,
    };
    if (opts.source) body['source'] = opts.source;
    if (opts.classification) body['classification'] = opts.classification;
    if (opts.dedupKey) body['dedup_key'] = opts.dedupKey;
    if (opts.severity) body['severity'] = opts.severity;
    if (opts.steps) body['steps'] = opts.steps;
    if (opts.errorMessage) body['error_message'] = opts.errorMessage;
    if (opts.context) body['context'] = opts.context;
    if (opts.reporterEmail) body['reporter_email'] = opts.reporterEmail;

    // Entity pointers. Default the database from the CLI's configured
    // default_database when not passed (the auto-capture analog) so a report
    // filed from a working session carries enough to investigate.
    const baseId = opts.baseId ?? loadConfig().default_database;
    if (baseId) body['base_id'] = baseId;
    if (opts.record_type) body['record_type'] = opts.record_type;
    if (opts.documentId) body['document_id'] = opts.documentId;
    if (opts.externalId) body['external_id'] = opts.externalId;
    if (opts.externalSource) body['external_source'] = opts.externalSource;
    if (opts.relationshipId) body['relationship_id'] = opts.relationshipId;

    const data = await client.request<ReportResponse>('POST', '/v1/reports', body);
    if (data.created) {
      console.log(`Report submitted (id: ${data.report_id}).`);
      console.log('Our team will review and follow up by email if you provided one.');
    } else {
      console.log(`A matching report already exists (id: ${data.report_id}) — your submission was merged into it.`);
    }
  });

reportCommand.command('list')
  .description('List your reports, newest first')
  .option('--status <s>', 'Filter by status (e.g. shipped — awaiting your verification)')
  .action(async function (this: Command, opts: { status?: string }) {
    const client = new ApiClient();
    const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : '';
    try {
      const rows = await client.request<ReporterSummary[]>('GET', `/v1/reports${qs}`);
      if (getFormat(this) === 'json') {
        console.log(JSON.stringify(rows, null, 2));
        return;
      }
      if (rows.length === 0) {
        console.log('No reports found.');
        return;
      }
      for (const r of rows) {
        console.log(`${r.report_id}  ${r.status.padEnd(10)}  ${r.classification ?? 'bug'}`);
      }
    } catch (err) {
      reportActionError(err);
    }
  });

reportCommand.command('get <report_id>')
  .description("Show one of your reports: status + the message thread")
  .action(async function (this: Command, reportId: string) {
    const client = new ApiClient();
    try {
      const data = await client.request<{ report: ReporterSummary; messages: ReportMessage[] }>(
        'GET',
        `/v1/reports/${encodeURIComponent(reportId)}`,
      );
      if (getFormat(this) === 'json') {
        console.log(JSON.stringify(data, null, 2));
        return;
      }
      const { report, messages } = data;
      console.log(`Report ${report.report_id}`);
      console.log(`  status:         ${report.status}`);
      console.log(`  classification: ${report.classification ?? 'bug'}`);
      if (report.contest_count > 0) console.log(`  contests:       ${report.contest_count}`);
      if (report.dismissal_final) console.log('  dismissal:      final (non-contestable)');
      if (messages.length > 0) {
        console.log('\n  Thread:');
        for (const m of messages) {
          console.log(`    [${m.author_role}] ${m.body}`);
        }
      }
      if (report.status === 'shipped') {
        console.log(`\n  This fix is shipped. Run \`rekor report accept ${report.report_id}\` if it works,`);
        console.log(`  or \`rekor report contest ${report.report_id} --reason "..."\` if it does not.`);
      }
    } catch (err) {
      reportActionError(err);
    }
  });

reportCommand.command('accept <report_id>')
  .description('Accept a shipped fix (→ addressed)')
  .action(async function (this: Command, reportId: string) {
    const client = new ApiClient();
    try {
      const data = await client.request<{ report: ReporterSummary }>(
        'POST',
        `/v1/reports/${encodeURIComponent(reportId)}/accept`,
      );
      console.log(`Accepted. Report ${data.report.report_id} → ${data.report.status}.`);
    } catch (err) {
      reportActionError(err);
    }
  });

reportCommand.command('contest <report_id>')
  .description('Contest a shipped fix or a dismissal (→ back to triage)')
  .requiredOption('--reason <text>', 'Why the fix/dismissal is wrong')
  .action(async function (this: Command, reportId: string, opts: { reason: string }) {
    const client = new ApiClient();
    try {
      const data = await client.request<{ report: ReporterSummary }>(
        'POST',
        `/v1/reports/${encodeURIComponent(reportId)}/contest`,
        { reason: opts.reason },
      );
      console.log(`Contested. Report ${data.report.report_id} → ${data.report.status} (back to triage).`);
    } catch (err) {
      reportActionError(err);
    }
  });
